import { Check, X } from 'lucide-react'
import { useAIChatStore } from '@/stores/aiChatStore'

/**
 * HITL 확인 다이얼로그 — 에이전트가 작업 변경 요청 시 표시
 * 입력 영역 바로 위에 고정 배치
 */
export function AIConfirmDialog() {
  const pendingApproval = useAIChatStore((s) => s.pendingApproval)
  const approveAction = useAIChatStore((s) => s.approveAction)
  const isLoading = useAIChatStore((s) => s.isLoading)

  if (!pendingApproval) return null

  const interruptValue = pendingApproval.interruptValue as Record<string, unknown> | string | undefined
  const description = typeof interruptValue === 'string'
    ? interruptValue
    : typeof interruptValue === 'object' && interruptValue
      ? formatInterruptValue(interruptValue)
      : '에이전트가 작업을 변경하려고 합니다.'

  return (
    <div className="border-t border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 px-4 py-3">
      <p className="text-sm font-medium text-amber-900 dark:text-amber-200 mb-1">
        작업 변경 확인
      </p>
      <p className="text-sm text-amber-800 dark:text-amber-300 mb-3 whitespace-pre-wrap">
        {description}
      </p>
      <div className="flex gap-2">
        <button
          onClick={() => approveAction(true)}
          disabled={isLoading}
          className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors disabled:opacity-50"
        >
          <Check className="w-4 h-4" />
          승인
        </button>
        <button
          onClick={() => approveAction(false)}
          disabled={isLoading}
          className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
        >
          <X className="w-4 h-4" />
          거부
        </button>
      </div>
    </div>
  )
}

const TOOL_LABELS: Record<string, string> = {
  create_task: '작업 생성',
  update_task: '작업 수정',
}

function formatInterruptValue(value: Record<string, unknown>): string {
  const parts: string[] = []

  // 설명
  if (value.description) parts.push(String(value.description))

  // pendingActions 표시 (agent.ts에서 전달하는 핵심 데이터)
  const pendingActions = value.pendingActions as Array<{ tool: string; args: Record<string, unknown> }> | undefined
  if (pendingActions?.length) {
    for (const pa of pendingActions) {
      const label = TOOL_LABELS[pa.tool] || pa.tool
      const details: string[] = []
      if (pa.args.title) details.push(`제목: ${pa.args.title}`)
      if (pa.args.priority && pa.args.priority !== 'none') details.push(`우선순위: ${pa.args.priority}`)
      if (pa.args.dueDate) details.push(`마감일: ${pa.args.dueDate}`)
      if (pa.args.status) details.push(`상태: ${pa.args.status}`)
      if (pa.args.memo) details.push(`메모: ${String(pa.args.memo).slice(0, 50)}`)

      parts.push(`- ${label}: ${details.length > 0 ? details.join(', ') : '(상세 정보 없음)'}`)
    }
  }

  // 기존 단순 필드 fallback
  if (!pendingActions?.length) {
    if (value.action) parts.push(String(value.action))
    if (value.title) parts.push(`제목: ${value.title}`)
    if (value.priority) parts.push(`우선순위: ${value.priority}`)
    if (value.dueDate) parts.push(`마감일: ${value.dueDate}`)
  }

  return parts.join('\n') || '작업 변경 요청'
}
